import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import { Link } from 'react-router-dom';
import Highlight from '../../components/base/Highlight';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { 
  createArticleStructuredData, 
  createFAQStructuredData, 
  createBreadcrumbStructuredData,
  createHowToStructuredData,
  createTableStructuredData
} from '../../utils/structuredData';

export default function BeginnerGuide() {
  // SEO設定
  usePageSEO({
    title: 'DMM FXは初心者でも大丈夫？メリット・デメリットを徹底解説',
    description: 'FX初心者がDMM FXで安心して始められる理由を徹底解説。業界最狭水準のスプレッド、24時間サポート、使いやすいツールなど6つのメリットと3つのデメリット、口座開設4ステップを詳しく紹介。初心者向けFAQも充実。',
    keywords: 'DMM FX,初心者,FX入門,メリット,デメリット,口座開設,スプレッド,サポート,デモ口座',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXは初心者でも大丈夫？メリット・デメリットを徹底解説',
        description: 'FX初心者が気になるDMM FXのメリット・デメリットを徹底解説。安心して始められる理由をお伝えします。',
        author: 'DMM FX情報局',
        datePublished: '2024-11-15',
        dateModified: new Date().toISOString().split('T')[0],
        image: 'https://public.readdy.ai/ai/img_res/20fafa00e32df4e34e250259e358b7bd.jpg',
        url: '/beginner-guide',
      }),
      createHowToStructuredData({
        name: 'FX初心者がDMM FXで取引を始める方法',
        description: '初心者でも安心してDMM FXで取引を始められる4つのステップを解説します。',
        totalTime: 'PT1D',
        estimatedCost: '0円（口座開設・維持費無料）',
        steps: [
          {
            name: '口座開設申込',
            text: 'スマホ・PCから最短5分で申込完了。本人確認書類とマイナンバーを準備しましょう。',
            image: 'https://readdy.ai/api/search-image?query=online%20account%20registration%20form%20smartphone%20document%20verification%20simple%20clean%20interface&width=800&height=600&seq=beginner-step1-001&orientation=landscape',
            url: '/account-opening',
          },
          {
            name: '審査・口座開設',
            text: '最短即日で審査完了。メールでログイン情報が届きます。',
            image: 'https://readdy.ai/api/search-image?query=email%20notification%20approval%20confirmation%20check%20mark%20success%20message%20clean%20modern%20design&width=800&height=600&seq=beginner-step2-001&orientation=landscape',
            url: '/account-opening',
          },
          {
            name: '入金',
            text: 'クイック入金なら即時反映。手数料無料で入金できます。',
            image: 'https://readdy.ai/api/search-image?query=bank%20transfer%20deposit%20money%20transaction%20instant%20payment%20interface%20clean%20professional%20design&width=800&height=600&seq=beginner-step3-001&orientation=landscape',
            url: '/deposit-withdrawal',
          },
          {
            name: '取引開始',
            text: 'デモ口座で練習してから本番取引へ。少額から始めましょう。',
            image: 'https://readdy.ai/api/search-image?query=trading%20chart%20candlestick%20forex%20market%20analysis%20beginner%20friendly%20interface%20clean%20modern%20design&width=800&height=600&seq=beginner-step4-001&orientation=landscape',
            url: '/beginner-guide',
          },
        ],
      }),
      createTableStructuredData({
        about: 'DMM FXと他社FX業者の比較表',
        columns: ['項目', 'DMM FX', '他社平均'],
        rows: [
          ['スプレッド(ドル円)', '0.2銭', '0.2-0.5銭'],
          ['最低取引単位', '1万通貨', '1,000通貨-1万通貨'],
          ['通貨ペア数', '21種類', '20-50種類'],
          ['サポート時間', '24時間', '9:00-17:00など'],
          ['取引ツール', '高機能', '業者による'],
          ['キャンペーン', '最大50万円', '業者による'],
        ],
      }),
      createFAQStructuredData([
        {
          question: 'FX初心者でも本当に大丈夫ですか?',
          answer: 'はい、大丈夫です。DMM FXは初心者向けのサポート体制が充実しており、デモ口座で練習もできます。24時間サポートもあるので、分からないことがあればすぐに相談できます。',
          url: '/beginner-guide',
        },
        {
          question: 'いくらから始められますか?',
          answer: 'DMM FXは1万通貨からの取引となります。ドル円の場合、レバレッジ25倍で約6万円から取引可能です。ただし、余裕を持って10万円程度の資金で始めることをおすすめします。',
          url: '/beginner-guide',
        },
        {
          question: '口座開設にどのくらい時間がかかりますか?',
          answer: '申込自体は最短5分で完了します。審査は最短即日で完了し、その日のうちに取引を始められる場合もあります。',
          url: '/beginner-guide',
        },
        {
          question: 'スマホだけで取引できますか?',
          answer: 'はい、できます。DMM FXのスマホアプリは高機能で使いやすく、口座開設から取引まで全てスマホで完結できます。',
          url: '/beginner-guide',
        }
      ]),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: 'FX初心者ガイド', url: '/fx-guide' },
        { name: '初心者でも大丈夫？', url: '/beginner-guide' },
      ]),
    ],
  });

  const merits = [
    {
      icon: 'ri-funds-line',
      title: '業界最狭水準のスプレッド',
      description: 'ドル円0.2銭、ユーロ円0.4銭など、取引コストを大幅に抑えられます。デイトレードをする方には特におすすめです。'
    },
    {
      icon: 'ri-smartphone-line',
      title: '使いやすい取引ツール',
      description: 'PC版の「DMMFX PLUS」とスマホアプリは直感的な操作性。初心者でもすぐに使いこなせる設計になっています。'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24時間サポート体制',
      description: '平日24時間、電話・メール・LINEで問い合わせ可能。初心者の疑問にも丁寧に対応してくれます。'
    },
    {
      icon: 'ri-gift-line',
      title: '充実のキャンペーン',
      description: '新規口座開設で最大50万円のキャッシュバック。取引量に応じてポイントも貯まります。'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'DMMグループの信頼性',
      description: '大手DMMグループが運営する安心感。高い約定力で、狙った価格で取引できます。'
    },
    {
      icon: 'ri-bank-line',
      title: '入出金手数料無料',
      description: 'クイック入金・出金手数料が無料。余計なコストをかけずに取引を始められます。'
    }
  ];

  const demerits = [
    {
      icon: 'ri-exchange-line',
      title: '通貨ペアが21種類',
      description: '他社と比べると通貨ペアの種類がやや少なめ。ただし、主要通貨ペアは全て揃っています。',
      solution: '初心者の方は主要通貨ペアで十分。慣れてきたら他社も検討しましょう。'
    },
    {
      icon: 'ri-percent-line',
      title: 'スワップポイントがやや低め',
      description: '長期保有でスワップポイントを狙う場合、他社の方が有利な場合があります。',
      solution: 'デイトレード・スイングトレードメインの方には影響なし。'
    },
    {
      icon: 'ri-user-line',
      title: '最低取引単位が1万通貨',
      description: '1,000通貨から取引できる業者と比べると、初期資金が多めに必要です。',
      solution: 'デモ口座で練習してから、余裕資金で始めましょう。'
    }
  ];

  const steps = [
    {
      number: '01',
      title: '口座開設申込',
      description: 'スマホ・PCから最短5分で申込完了。本人確認書類とマイナンバーを準備しましょう。',
      icon: 'ri-file-list-3-line'
    },
    {
      number: '02',
      title: '審査・口座開設',
      description: '最短即日で審査完了。メールでログイン情報が届きます。',
      icon: 'ri-shield-check-line'
    },
    {
      number: '03',
      title: '入金',
      description: 'クイック入金なら即時反映。手数料無料で入金できます。',
      icon: 'ri-bank-line'
    },
    {
      number: '04',
      title: '取引開始',
      description: 'デモ口座で練習してから本番取引へ。少額から始めましょう。',
      icon: 'ri-line-chart-line'
    }
  ];

  const faqs = [
    {
      question: 'FX初心者でも本当に大丈夫ですか?',
      answer: 'はい、大丈夫です。DMM FXは初心者向けのサポート体制が充実しており、デモ口座で練習もできます。24時間サポートもあるので、分からないことがあればすぐに相談できます。'
    },
    {
      question: 'いくらから始められますか?',
      answer: 'DMM FXは1万通貨からの取引となります。ドル円の場合、レバレッジ25倍で約6万円から取引可能です。ただし、余裕を持って10万円程度の資金で始めることをおすすめします。'
    },
    {
      question: '口座開設にどのくらい時間がかかりますか?',
      answer: '申込自体は最短5分で完了します。審査は最短即日で完了し、その日のうちに取引を始められる場合もあります。'
    },
    {
      question: 'スマホだけで取引できますか?',
      answer: 'はい、できます。DMM FXのスマホアプリは高機能で使いやすく、口座開設から取引まで全てスマホで完結できます。'
    }
  ];

  const comparison = [
    { feature: 'スプレッド(ドル円)', dmm: '0.2銭', other: '0.2-0.5銭' },
    { feature: '最低取引単位', dmm: '1万通貨', other: '1,000通貨-1万通貨' },
    { feature: '通貨ペア数', dmm: '21種類', other: '20-50種類' },
    { feature: 'サポート時間', dmm: '24時間', other: '9:00-17:00など' },
    { feature: '取引ツール', dmm: '高機能', other: '業者による' },
    { feature: 'キャンペーン', dmm: '最大50万円', other: '業者による' }
  ];

  // 強化版CTAボタンコンポーネント
  const CTAButton = () => (
    <div className="text-center">
      <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
        <i className="ri-gift-line"></i>
        <span>👉 今だけ最大50万円キャッシュバック</span>
      </div>
      <div>
        <a 
          href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
          rel="nofollow"
          className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-8 py-5 rounded-xl text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
        >
          👉 今だけ最大50万円キャッシュバック<br />
          無料でDMM FX口座を開設する
        </a>
        <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
      </div>
      <p className="text-sm text-gray-500 mt-3">口座開設・維持費無料 / 最短5分で申込完了</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX初心者ガイド', url: '/fx-guide' },
            { name: '初心者でも大丈夫？', url: '/beginner-guide' }
          ]} />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative pt-20 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-2 rounded-full mb-4 md:mb-6 shadow-sm">
              <i className="ri-user-star-line text-teal-600 text-base md:text-lg"></i>
              <span className="text-xs md:text-sm font-medium text-gray-700">初心者必見</span>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6 leading-tight">
              DMM FXは初心者でも大丈夫?
            </h1>
            
            <p className="text-sm md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed px-2">
              FX初心者が気になるメリット・デメリットを徹底解説。<br className="hidden md:block" />
              安心して始められる理由をお伝えします。
            </p>

            {/* CTA①：冒頭 */}
            <CTAButton />
          </div>
        </div>
      </section>

      {/* Merits Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">DMM FXのメリット</h2>
            <p className="text-gray-600 text-sm md:text-base">初心者におすすめできる6つの理由</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {merits.map((merit, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 md:p-6 border border-gray-200 hover:shadow-lg transition-all">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                  <i className={`${merit.icon} text-white text-lg md:text-xl`}></i>
                </div>
                <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3">
                  <Highlight variant="marker" color="red">{merit.title}</Highlight>
                </h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{merit.description}</p>
              </div>
            ))}
          </div>

          {/* CTA②：メリット後 */}
          <div className="mt-10 md:mt-12">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* Demerits Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">DMM FXのデメリット</h2>
            <p className="text-gray-600 text-sm md:text-base">知っておきたい注意点と解決策</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {demerits.map((demerit, index) => (
              <div key={index} className="bg-white rounded-xl p-5 md:p-6 border border-gray-200">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                  <i className={`${demerit.icon} text-gray-600 text-lg md:text-xl`}></i>
                </div>
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 md:mb-3">{demerit.title}</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed mb-3">{demerit.description}</p>
                <div className="bg-teal-50 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <i className="ri-lightbulb-line text-teal-600 text-sm md:text-base mt-0.5"></i>
                    <p className="text-xs leading-relaxed">
                      <Highlight variant="bold" color="red" className="text-xs">解決策:</Highlight> {demerit.solution}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 利用者の声へのリンク */}
          <div className="mt-8 md:mt-12 bg-white rounded-xl p-4 md:p-6 border-l-4 border-teal-500 shadow-md">
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              実際の利用者の声や評判については、こちらの記事で詳しくまとめています。<br />
              → <Link to="/reviews" className="text-teal-600 font-semibold hover:text-teal-700 underline cursor-pointer">「DMM FXの口コミ・評判は？実際どうなのか調査」</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">初心者が始める4ステップ</h2>
            <p className="text-gray-600 text-sm md:text-base">口座開設から取引開始まで</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-all h-full">
                  <div className="text-3xl md:text-5xl font-bold text-teal-100 mb-2 md:mb-4">{step.number}</div>
                  <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-2 md:mb-4">
                    <i className={`${step.icon} text-white text-sm md:text-xl`}></i>
                  </div>
                  <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">{step.title}</h3>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed hidden sm:block">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-teal-300 text-2xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA③：ステップ後 */}
          <div className="text-center mt-10 md:mt-12">
            <CTAButton />
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">他社との比較</h2>
            <p className="text-gray-600 text-sm md:text-base">DMM FXの強みが一目で分かる</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full text-sm md:text-base">
                <thead className="bg-gradient-to-r from-teal-500 to-emerald-600">
                  <tr>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-white">項目</th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-white">DMM FX</th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-white">他社平均</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {comparison.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-medium text-gray-900">{item.feature}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold">
                        <Highlight variant="bold" color="red" className="text-xs md:text-sm">{item.dmm}</Highlight>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm text-gray-600">{item.other}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">よくある質問</h2>
            <p className="text-gray-600 text-sm md:text-base">初心者の方からよく寄せられる質問</p>
          </div>

          <div className="space-y-3 md:space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 md:p-6 border border-gray-200">
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xs md:text-sm">Q</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm md:text-base font-bold text-gray-900 mb-2 md:mb-3">{faq.question}</h3>
                    <div className="flex items-start space-x-3 md:space-x-4">
                      <div className="w-7 h-7 md:w-8 md:h-8 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-600 font-bold text-xs md:text-sm">A</span>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Articles Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">関連記事</h2>
            <p className="text-gray-600 text-sm md:text-base">もっと詳しく知りたい方へ</p>
          </div>

          {/* 口座開設手順へのリンク */}
          <div className="mb-6 md:mb-8 bg-white rounded-xl p-4 md:p-6 border-l-4 border-emerald-500 shadow-md max-w-3xl mx-auto">
            <p className="text-sm md:text-base text-gray-700 leading-relaxed">
              口座開設の具体的な手順は、こちらで画像付きで解説しています。<br />
              → <Link to="/account-opening" className="text-emerald-600 font-semibold hover:text-emerald-700 underline cursor-pointer">「DMM FXの口座開設手順を画像付きで解説」</Link>
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            <Link to="/fx-basics" className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-book-open-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">FX超入門ガイド</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden sm:block">FXの基本から学びたい方へ、わかりやすく解説</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 md:ml-2"></i>
              </div>
            </Link>

            <Link to="/app-guide" className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-smartphone-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">アプリの使い方</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden sm:block">スマホアプリの操作方法を画像付きで解説</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 md:ml-2"></i>
              </div>
            </Link>

            <Link to="/spread" className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-funds-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">スプレッド比較</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden sm:block">取引コストを他社と徹底比較</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 md:ml-2"></i>
              </div>
            </Link>

            <Link to="/safety" className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 hover:shadow-lg transition-all cursor-pointer">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                <i className="ri-shield-check-line text-white text-lg md:text-xl"></i>
              </div>
              <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">安全性を解説</h3>
              <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 hidden sm:block">金融庁登録・信託保全など詳しく解説</p>
              <div className="flex items-center text-teal-600 text-xs md:text-sm font-semibold">
                <span>詳しく見る</span>
                <i className="ri-arrow-right-line ml-1 md:ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-teal-500 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            初心者でも安心して始められる
          </h2>
          <p className="text-sm md:text-lg text-white/90 mb-6 md:mb-8 leading-relaxed">
            DMM FXなら充実したサポート体制で、あなたのFXデビューを全力で応援します。<br className="hidden md:block" />
            まずは無料で口座開設してみましょう。
          </p>
          
          {/* CTA④：最終CTA */}
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-400 to-orange-500 text-gray-900 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-bold mb-4 animate-pulse">
            <i className="ri-gift-line"></i>
            <span>👉 今だけ最大50万円キャッシュバック</span>
          </div>
          <div className="block mb-3">
            <a 
              href="https://px.a8.net/svt/ejp?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" 
              rel="nofollow"
              className="inline-block bg-gradient-to-b from-orange-500 to-orange-600 text-white px-6 md:px-8 py-4 md:py-5 rounded-xl text-base md:text-xl font-bold hover:scale-105 hover:-translate-y-1 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-[0_6px_18px_rgba(0,0,0,0.25)]"
            >
              👉 今だけ最大50万円キャッシュバック<br />
              無料でDMM FX口座を開設する
            </a>
            <img border="0" width="1" height="1" src="https://www13.a8.net/0.gif?a8mat=4AX8OA+BW2OQA+1WP2+6JC82" alt="" style={{ display: 'none' }} />
          </div>
          <p className="text-xs md:text-sm text-white/80">口座開設・維持費無料 / 最短5分で申込完了</p>
        </div>
      </section>

      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}